import { createPortal } from "preact/compat";
import { useApp } from "../app-context.js";
import { moveColor, moveReviewId } from "../lib/view-model.js";
import {
  clearComparisonFocus,
  clearMoveFocus,
  flashComparison,
  focusComparison,
  focusMove,
} from "../lib/focus.js";
import { Segments, useGithubLine } from "./diff-files.js";
import { SafeText } from "./safe-text.js";
import type {
  CodeMove,
  ComparisonLine,
  FollowMove,
  MoveLayout,
  MovePreviewLayout,
  MoveRole,
} from "../types.js";

interface MoveProps {
  layout: MoveLayout;
  onFollowMove: FollowMove;
}

export function MoveLayer({ layout, onFollowMove }: MoveProps) {
  const { reviewed, showReviewed } = useApp();
  return (
    <svg
      class="move-layer"
      id="move-layer"
      aria-hidden="true"
      width={layout.width}
      height={layout.height}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <marker id="arrowhead" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M 0 0 L 8 4 L 0 8 Z" />
        </marker>
      </defs>
      <g id="move-paths">
        {layout.arrows.map(({ bend, from, move, to }) => {
          const reviewedClass = reviewed.has(moveReviewId(move.id)) && showReviewed
            ? " reviewed-visible"
            : "";
          return (
            <g key={move.id}>
              <path
                d={`M ${from.x} ${from.y} C ${from.x + bend} ${from.y}, ${to.x - bend} ${to.y}, ${to.x} ${to.y}`}
                class={`move-path${reviewedClass}`}
                data-move-id={move.id}
                data-review-id={moveReviewId(move.id)}
                stroke={moveColor(move.id)}
                marker-end="url(#arrowhead)"
                tabIndex={0}
                onMouseEnter={() => focusMove(Number(move.id))}
                onMouseLeave={clearMoveFocus}
                onClick={() => onFollowMove(move.id, "target")}
              />
              <circle
                cx={from.x}
                cy={from.y}
                r="3.5"
                fill={moveColor(move.id)}
                class={`move-endpoint${reviewedClass}`}
                data-move-id={move.id}
                data-review-id={moveReviewId(move.id)}
              />
              <circle
                cx={to.x}
                cy={to.y}
                r="3.5"
                fill={moveColor(move.id)}
                class={`move-endpoint${reviewedClass}`}
                data-move-id={move.id}
                data-review-id={moveReviewId(move.id)}
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

interface PreviewLineProps {
  line: ComparisonLine;
  move: CodeMove;
  onFollowMove: FollowMove;
  role: MoveRole;
}

function PreviewLine({ line, move, onFollowMove, role }: PreviewLineProps) {
  const { comparisons } = useApp();
  const isTarget = role === "target";
  const alignmentId = line.alignmentId;
  const comparisonId = `${move.id}:${alignmentId}`;
  const side = isTarget ? "new" : "old";
  const lineNumber = isTarget ? line.targetNumber : line.sourceNumber;
  const span = move[role];
  const rangeKey = comparisons.realLocations.get(`${role}:${comparisonId}`)?.rangeKey
    ?? `${span.path}:${role}:${span.startLine}-${span.endLine}`;
  const github = useGithubLine(span.path, side, lineNumber, rangeKey);
  const githubClasses = github
    ? `${github.selected ? " comment-range-selected" : ""}${github.startSelected ? " comment-range-start" : ""}${github.endSelected ? " comment-range-end" : ""}`
    : "";
  const marker = role === "target" && line.kind === "added"
    ? "+"
    : role === "source" && line.kind === "deleted"
      ? "−"
      : line.kind === "changed" ? "~" : " ";

  const clearFocus = () => {
    clearMoveFocus();
    clearComparisonFocus(comparisonId);
  };
  const jumpToRealLine = () => {
    const target = document.querySelector(`.line-cell.move-${role}[data-comparison-id="${CSS.escape(comparisonId)}"]`);
    if (!target) {
      onFollowMove(move.id, role);
      return;
    }
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    flashComparison(comparisonId, target as HTMLElement);
  };

  return (
    <div
      class={`preview-line comparison-${line.kind}${githubClasses}`}
      data-comparison-id={comparisonId}
      data-github-path={github?.anchorPath}
      data-github-side={github ? side : undefined}
      data-github-line={github ? lineNumber ?? undefined : undefined}
      data-github-range={github ? rangeKey : undefined}
      tabIndex={0}
      onMouseEnter={(event) => {
        focusMove(Number(move.id));
        focusComparison(comparisonId, event.currentTarget);
      }}
      onMouseLeave={clearFocus}
      onFocus={(event) => {
        focusMove(Number(move.id));
        focusComparison(comparisonId, event.currentTarget);
      }}
      onBlur={clearFocus}
      onClick={(event) => {
        if ((event.target as Element).closest("a, button, .comment-select-number") || !window.getSelection()?.isCollapsed) return;
        jumpToRealLine();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget) jumpToRealLine();
      }}
    >
      <span
        class={`line-number${github ? " comment-select-number" : ""}`}
        aria-label={github ? `Select ${side} line ${lineNumber} for a GitHub comment` : undefined}
        onPointerDown={github?.onPointerDown}
        onPointerEnter={github?.onPointerEnter}
        onClick={github?.onNumberClick}
      >
        {lineNumber ?? ""}
      </span>
      <span class="line-marker">{marker}</span>
      <code>
        <span class="code-content">
          <Segments comparisonId={comparisonId} segments={isTarget ? line.target : line.source} />
        </span>
      </code>
      {github && (
        <a
          class="comment-line-button"
          href={github.href}
          data-label={github.count > 1 ? `Comment ${github.count} lines` : "Comment"}
          target="_blank"
          rel="noreferrer"
          aria-label={github.count > 1 ? `Open ${github.count} selected lines on GitHub to comment` : `Open ${side} line ${lineNumber} on GitHub to comment`}
          onClick={(event) => event.stopPropagation()}
        />
      )}
    </div>
  );
}

function MovePreviewLines({ move, onFollowMove, role }: { move: CodeMove; onFollowMove: FollowMove; role: MoveRole }) {
  const { comparisons, hideUnchangedMoves, setHideUnchangedMoves } = useApp();
  const lines = [];
  for (let index = 0; index < move.comparison.length; index += 1) {
    const line = move.comparison[index];
    const comparisonId = `${move.id}:${line.alignmentId}`;
    if (hideUnchangedMoves && comparisons.collapsedComparisonIds.has(comparisonId)) {
      let count = 1;
      while (index + count < move.comparison.length) {
        const next = move.comparison[index + count];
        const nextId = `${move.id}:${next.alignmentId}`;
        if (!comparisons.collapsedComparisonIds.has(nextId)) break;
        count += 1;
      }
      lines.push(
        <button type="button" class="preview-collapse-placeholder" onClick={() => setHideUnchangedMoves(false)} key={`collapsed:${comparisonId}`}>
          ⋯ {count} unchanged moved {count === 1 ? "line" : "lines"} hidden
        </button>,
      );
      index += count - 1;
      continue;
    }
    lines.push(<PreviewLine line={line} move={move} onFollowMove={onFollowMove} role={role} key={`${line.alignmentId}:${index}`} />);
  }
  return lines;
}

function MovePreview({ item, onFollowMove }: { item: MovePreviewLayout; onFollowMove: FollowMove }) {
  const { showReviewed, reviewed } = useApp();
  const { left, move, role, top, width } = item;
  const isTarget = role === "target";
  const span = move[role];
  const hidden = reviewed.has(moveReviewId(move.id)) && !showReviewed;
  const reviewedClass = reviewed.has(moveReviewId(move.id)) && showReviewed
    ? " reviewed-visible"
    : "";
  return (
    <section
      class={`move-preview move-preview-${role}${reviewedClass}`}
      data-move-id={move.id}
      data-review-id={moveReviewId(move.id)}
      hidden={hidden}
      style={{
        "--move-color": moveColor(move.id),
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
      }}
    >
      <button
        type="button"
        class="move-preview-header"
        title={isTarget ? "Jump to the destination in its real location" : "Jump back to the source in its real location"}
        onClick={() => onFollowMove(move.id, role)}
        onMouseEnter={() => focusMove(Number(move.id))}
        onMouseLeave={clearMoveFocus}
      >
        <span>{isTarget ? "Moved to" : "Moved from"}</span>
        <code title={`${span.path}:${span.startLine}`}><SafeText text={`${span.path}:${span.startLine}`} /></code>
        <b>{isTarget ? "view ↘" : "view ↖"}</b>
      </button>
      <div class="move-preview-body">
        <MovePreviewLines move={move} onFollowMove={onFollowMove} role={role} />
      </div>
    </section>
  );
}

export function MovePreviews({ layout, onFollowMove }: MoveProps) {
  return layout.previews.map((item) => createPortal(
    <MovePreview item={item} onFollowMove={onFollowMove} key={`${item.move.id}:${item.role}`} />,
    item.container,
  ));
}
